import { Dialog, DialogWindow, MessageDialog, QuestionDialog, useDialogManager } from '../../../../widgets/dialog';
import React, { FC, Suspense, useMemo, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import AreaDesigner from 'src/components/widgets/areaDesigner/areaDesigner';
import Loader from 'src/components/widgets/loader';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Area } from '../../../../widgets/areaDesigner/areaDesigner.model';
import { DisabledModelSide, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide } from '../../state/model/modelSide';
import { printAreaEditorDialogMutation } from './__generated__/printAreaEditorDialogMutation.graphql';
import { printAreaEditorDialogQuery } from './__generated__/printAreaEditorDialogQuery.graphql';

const LoaderWrapper = styled.div`
    position: relative;
    top: 34%;
    text-align: -webkit-center;
`;

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    margin: 0 80px;
`;

interface PrintAreaEditorDialogProps {
    modelColor: ModelColor;
    modelSide: ModelSide;
    modelOptions: ModelOptions | null;
    disabledModelSides: DisabledModelSide[];
    onSave: (modelSide: ModelSide, modelOptions: ModelOptions | null) => void;
    onClose: () => void;
}

const PrintAreaEditorDialogContent: FC<PrintAreaEditorDialogProps> = (props) => {
    const [modelSide, setModelSide] = useState(props.modelSide);

    const { showDialog } = useDialogManager();

    const query = useLazyLoadQuery<printAreaEditorDialogQuery>(
        graphql`
            query printAreaEditorDialogQuery {
                printAreaTemplates {
                    templateID
                    name
                    width
                    height
                }
            }
        `,
        {}
    );

    const [commiCreateTemplate, isSavingTemplate] = useMutation<printAreaEditorDialogMutation>(graphql`
        mutation printAreaEditorDialogMutation($input: PrintAreaTemplateCreateInput!) {
            createPrintAreaTemplate(input: $input) {
                printAreaTemplate {
                    templateID
                    name
                    width
                    height
                }
            }
        }
    `);

    const findTemplateForArea = (area: Area) => {
        const areaWidthCm = area.boundsWidth / modelSide.ppcm;
        const areaHeightCm = area.boundsHeight / modelSide.ppcm;
        return query.printAreaTemplates.find((x) => Math.abs(x!.width - areaWidthCm) < 0.01 && Math.abs(x!.height - areaHeightCm) < 0.01);
    };

    const handleSaveClick = (modelOptions: ModelOptions | null) => {
        modelSide.areas = modelSide.areas.filter((x) => x.boundsWidth !== 0 && x.boundsHeight !== 0);

        if (modelSide.areas.length === 0) {
            showDialog('no-areas', <MessageDialog message={T._('Add at last one print area.', 'Admin')} />);
            return;
        }

        for (const area of modelSide.areas) {
            const template = findTemplateForArea(area);
            if (!template) {
                const areaWidthCm = area.boundsWidth / modelSide.ppcm;
                const areaHeightCm = area.boundsHeight / modelSide.ppcm;

                commiCreateTemplate({
                    onCompleted: (response) => null,
                    onError: (response) => null,
                    updater: (store) => store.invalidateStore(),
                    variables: {
                        input: {
                            name: area.name != '' ? area.name : T._('Custom size', 'Admin'),
                            width: areaWidthCm,
                            height: areaHeightCm
                        }
                    }
                });
            }
        }

        props.onSave(modelSide, modelOptions);
    };

    return (
        <AreaDesigner
            css={`height: 100%; !important; width: 100% !important;`}
            modelSide={modelSide}
            modelColor={props.modelColor}
            modelOptions={props.modelOptions}
            disabledModelSides={props.disabledModelSides}
            onModelSideChanged={(modelSide) => setModelSide(modelSide)}
            onSaveClick={handleSaveClick}
        />
    );
};

export const PrintAreaEditorDialog: FC<PrintAreaEditorDialogProps> = (props) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { state } = useProductConfigurationState();

    const side = useMemo(
        () => (props.modelSide ? state.sides.find((x) => x.guid == props.modelSide.originalSideGuid) : null),
        [props.modelSide?.originalSideGuid]
    );

    const handleOnClose = () => {
        showDialog(
            'exit',
            <QuestionDialog
                message={T._('Exit without saving?', 'Admin')}
                onYesClick={() => {
                    closeDialog('exit');
                    props.onClose();
                }}
            />
        );
    };

    return (
        <Dialog title={props.modelColor.name + ' | ' + side!.name} onClose={handleOnClose} windowDecorator={CustomDialogWindow} buttons={[]}>
            <Suspense
                fallback={
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                }
            >
                <PrintAreaEditorDialogContent {...props} />
            </Suspense>
        </Dialog>
    );
};
